/**
 * Черновик call и тост ученикам. Живёт в корне сканера, не на посадке.
 */

import { useEffect } from 'react';

import { useSession } from '../access/session';
import { type DeskCall as Call, type Draft, POLL_MS, deskForkId, openCall, openCallInCalc, openCallInFeed, pullCall, toastLine, toastRoi, useDeskCall } from './deskCall';
import { type Meta, venueName } from './meta';
import { useScreen } from './store';
import { logoOf } from './venues';

function patchLeg(draft: Draft, index: number, patch: Partial<Draft['legs'][number]>): Draft {
  return {
    ...draft,
    legs: draft.legs.map((leg, i) => (i === index ? { ...leg, ...patch } : leg)),
  };
}

function Compose(): React.ReactElement | null {
  const draft = useDeskCall((s) => s.draft);
  const error = useDeskCall((s) => s.error);
  const sending = useDeskCall((s) => s.sending);
  const setDraft = useDeskCall((s) => s.setDraft);
  const close = useDeskCall((s) => s.close);
  const send = useDeskCall((s) => s.send);
  if (draft === null) return null;

  return (
    <div className="nx-desk-mask" role="presentation" onClick={close}>
      <form
        className="nx-desk-form"
        onClick={(event) => event.stopPropagation()}
        onSubmit={(event) => {
          event.preventDefault();
          void send();
        }}
      >
        <header>
          <b>call ученикам</b>
          <button type="button" onClick={close}>
            закрыть
          </button>
        </header>
        <label>
          матч
          <input
            value={draft.game}
            onChange={(event) => setDraft({ ...draft, game: event.target.value })}
            autoComplete="off"
          />
        </label>
        <label>
          ставка
          <input
            value={draft.bet}
            onChange={(event) => setDraft({ ...draft, bet: event.target.value })}
            autoComplete="off"
          />
        </label>
        {draft.legs.map((leg, i) => (
          <fieldset key={i}>
            <legend>нога {i + 1}</legend>
            <input
              placeholder="контора"
              value={leg.source}
              onChange={(event) => setDraft(patchLeg(draft, i, { source: event.target.value }))}
              autoComplete="off"
            />
            <input
              placeholder="ссылка"
              value={leg.url}
              onChange={(event) => setDraft(patchLeg(draft, i, { url: event.target.value }))}
              autoComplete="off"
            />
            <input
              placeholder="кэф"
              value={leg.odd}
              onChange={(event) => setDraft(patchLeg(draft, i, { odd: event.target.value }))}
              inputMode="decimal"
              autoComplete="off"
            />
            {draft.legs.length > 1 && (
              <button
                type="button"
                onClick={() =>
                  setDraft({ ...draft, legs: draft.legs.filter((_, j) => j !== i) })
                }
              >
                убрать
              </button>
            )}
          </fieldset>
        ))}
        {draft.legs.length < 4 && (
          <button
            type="button"
            onClick={() =>
              setDraft({
                ...draft,
                legs: [...draft.legs, { source: '', url: '', odd: '', title: '' }],
              })
            }
          >
            ещё нога
          </button>
        )}
        {error !== null && <p className="nx-desk-err">{error}</p>}
        <footer>
          <button type="submit" disabled={sending}>
            {sending ? 'шлю…' : 'позвать'}
          </button>
        </footer>
      </form>
    </div>
  );
}

function CalcMark(): React.ReactElement {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <rect x="5" y="3" width="14" height="18" />
      <rect x="8" y="6" width="8" height="3" />
      <path d="M8 13h.01M12 13h.01M16 13h.01M8 16.5h.01M12 16.5h.01M16 16.5h.01" />
    </svg>
  );
}

export function DeskCallToast({
  call,
  meta,
  roi,
}: {
  call: Call;
  meta: Meta;
  roi?: string | number | null;
}): React.ReactElement {
  const dismiss = useDeskCall((s) => s.dismiss);
  const tapeNote = useDeskCall((s) => s.tapeNote);
  const forkId = call.forkId;
  const liveRoi = useScreen((s) => (forkId !== null ? s.forks.get(forkId)?.roi : undefined));
  const plus = toastRoi(roi !== undefined ? roi : liveRoi);
  return (
    <aside className={`nx-desk-toast${call.kind === 'practice' ? ' nx-desk-toast--practice' : ''}`} role="status">
      <button type="button" className="nx-desk-toast__x" aria-label="закрыть" onClick={dismiss}>
        ×
      </button>
      <strong>{toastLine(call)}</strong>
      <span>
        {call.game}
        {call.bet ? ` · ${call.bet}` : ''}
      </span>
      {tapeNote !== null && tapeNote !== 'отправили' && (
        <span className="nx-desk-err">{tapeNote}</span>
      )}
      <div className="nx-desk-toast__books">
        {call.legs.map((leg) => {
          const logo = logoOf(leg.source);
          return (
            <button
              key={leg.source + leg.url}
              type="button"
              className="nx-desk-toast__book"
              onClick={() => openCall({ ...call, legs: [leg] })}
            >
              {logo !== null && <img className="nx-fk__mk" src={logo} alt="" />}
              {venueName(meta, leg.source)}
            </button>
          );
        })}
      </div>
      <div className="nx-desk-toast__act">
        <button
          type="button"
          className="nx-desk-toast__open"
          onClick={() => {
            if (openCallInFeed(call)) dismiss();
          }}
        >
          открыть
        </button>
        {deskForkId(call) !== null && (
          <button
            type="button"
            className="nx-desk-toast__calc"
            onClick={() => openCallInCalc(call)}
            aria-label="в калькулятор"
            title="в калькулятор"
          >
            <CalcMark />
          </button>
        )}
        {plus !== null && <em className="nx-desk-toast__roi">{plus}</em>}
      </div>
    </aside>
  );
}

export function DeskCall(): React.ReactElement | null {
  const access = useSession((s) => s.me.access);
  const known = useSession((s) => s.known);
  const inbox = useDeskCall((s) => s.inbox);
  const hear = useDeskCall((s) => s.hear);
  const meta = useScreen((s) => s.meta);

  useEffect(() => {
    if (!known || !access) return;
    let alive = true;
    const tick = (): void => {
      void pullCall().then((call) => {
        if (alive) hear(call);
      });
    };
    tick();
    const id = window.setInterval(tick, POLL_MS);
    return () => {
      alive = false;
      window.clearInterval(id);
    };
  }, [access, known, hear]);

  if (!known || !access) return <Compose />;
  return (
    <>
      <Compose />
      {inbox !== null && <DeskCallToast call={inbox} meta={meta} />}
    </>
  );
}
